// Unit conversion system: Imperial <-> Metric

const VOLUME_CONVERSIONS = {
  // Imperial -> Metric (milliliters)
  tsp: { ml: 4.929, label: 'teaspoon' },
  tbsp: { ml: 14.787, label: 'tablespoon' },
  'fl oz': { ml: 29.574, label: 'fluid ounce' },
  cup: { ml: 236.588, label: 'cup' },
  cups: { ml: 236.588, label: 'cups' },
  pint: { ml: 473.176, label: 'pint' },
  quart: { ml: 946.353, label: 'quart' },
  gallon: { ml: 3785.41, label: 'gallon' },
};

const WEIGHT_CONVERSIONS = {
  // Imperial -> Metric (grams)
  oz: { g: 28.3495, label: 'ounce' },
  lb: { g: 453.592, label: 'pound' },
  lbs: { g: 453.592, label: 'pounds' },
};

const TEMPERATURE_OFFSET = 32;
const TEMPERATURE_RATIO = 5 / 9;

// Metric display preferences: pick the most readable unit
function bestMetricVolume(ml) {
  if (ml >= 1000) return { qty: ml / 1000, unit: 'L' };
  if (ml >= 1) return { qty: ml, unit: 'ml' };
  return { qty: ml, unit: 'ml' };
}

function bestMetricWeight(g) {
  if (g >= 1000) return { qty: g / 1000, unit: 'kg' };
  return { qty: g, unit: 'g' };
}

function bestImperialVolume(ml) {
  if (ml >= 3785) return { qty: ml / 3785.41, unit: 'gallon' };
  if (ml >= 946) return { qty: ml / 946.353, unit: 'quart' };
  if (ml >= 236) return { qty: ml / 236.588, unit: 'cup' };
  if (ml >= 14.7) return { qty: ml / 14.787, unit: 'tbsp' };
  return { qty: ml / 4.929, unit: 'tsp' };
}

function bestImperialWeight(g) {
  if (g >= 453) return { qty: g / 453.592, unit: 'lb' };
  return { qty: g / 28.3495, unit: 'oz' };
}

// Smart rounding for cooking measurements
function smartRound(value) {
  if (value >= 100) return Math.round(value);
  if (value >= 10) return Math.round(value * 10) / 10;
  if (value >= 1) return Math.round(value * 100) / 100;
  return Math.round(value * 100) / 100;
}

/**
 * Convert a single ingredient between unit systems
 * @param {Object} ingredient - { qty, unit, item, group }
 * @param {'metric'|'imperial'} targetSystem
 * @returns {Object} converted ingredient
 */
export function convertIngredient(ingredient, targetSystem) {
  const { qty, unit } = ingredient;
  if (!qty || !unit) return ingredient;

  const unitLower = unit.toLowerCase().trim();

  if (targetSystem === 'metric') {
    // Convert imperial to metric
    if (VOLUME_CONVERSIONS[unitLower]) {
      const ml = qty * VOLUME_CONVERSIONS[unitLower].ml;
      const best = bestMetricVolume(ml);
      return { ...ingredient, qty: smartRound(best.qty), unit: best.unit };
    }
    if (WEIGHT_CONVERSIONS[unitLower]) {
      const g = qty * WEIGHT_CONVERSIONS[unitLower].g;
      const best = bestMetricWeight(g);
      return { ...ingredient, qty: smartRound(best.qty), unit: best.unit };
    }
  }

  if (targetSystem === 'imperial') {
    // Convert metric to imperial
    if (unitLower === 'ml') {
      const best = bestImperialVolume(qty);
      return { ...ingredient, qty: smartRound(best.qty), unit: best.unit };
    }
    if (unitLower === 'l') {
      const best = bestImperialVolume(qty * 1000);
      return { ...ingredient, qty: smartRound(best.qty), unit: best.unit };
    }
    if (unitLower === 'g') {
      const best = bestImperialWeight(qty);
      return { ...ingredient, qty: smartRound(best.qty), unit: best.unit };
    }
    if (unitLower === 'kg') {
      const best = bestImperialWeight(qty * 1000);
      return { ...ingredient, qty: smartRound(best.qty), unit: best.unit };
    }
  }

  // Unit not recognized or already in target system - return as-is
  return ingredient;
}

/**
 * Convert all ingredients in a recipe
 * @param {Array} ingredients
 * @param {'metric'|'imperial'} targetSystem
 * @returns {Array} converted ingredients
 */
export function convertIngredients(ingredients, targetSystem) {
  return ingredients.map(ing => convertIngredient(ing, targetSystem));
}

/**
 * Convert temperature between F and C
 * @param {number} temp
 * @param {'F'|'C'} from
 * @returns {{ value: number, unit: string }}
 */
export function convertTemperature(temp, from) {
  if (from === 'F') {
    return { value: Math.round((temp - TEMPERATURE_OFFSET) * TEMPERATURE_RATIO), unit: '°C' };
  }
  return { value: Math.round(temp / TEMPERATURE_RATIO + TEMPERATURE_OFFSET), unit: '°F' };
}

/**
 * Convert temperature references in step instructions
 * @param {string} instruction
 * @param {'metric'|'imperial'} targetSystem
 * @returns {string} instruction with converted temperatures
 */
export function convertStepTemperatures(instruction, targetSystem) {
  if (targetSystem === 'metric') {
    // Convert °F to °C
    return instruction.replace(/(\d+)°F/g, (match, temp) => {
      const c = convertTemperature(parseInt(temp), 'F');
      return `${c.value}°C`;
    });
  }
  // Convert °C to °F
  return instruction.replace(/(\d+)°C/g, (match, temp) => {
    const f = convertTemperature(parseInt(temp), 'C');
    return `${f.value}°F`;
  });
}

// Unit system labels for display
export const UNIT_SYSTEMS = {
  imperial: { label: 'Imperial (US)', shortLabel: 'US' },
  metric: { label: 'Metric', shortLabel: 'Metric' },
};
